import { Injectable, inject, signal, computed, effect } from '@angular/core';
import { Observable, from, lastValueFrom } from 'rxjs';
import { MsalFactoryService } from '@/app/auth/msal-factory.service';
import { Subject, filter, takeUntil } from 'rxjs';
import { AccountInfo, AuthenticationResult, EventMessage, EventType, InteractionStatus, RedirectRequest } from '@azure/msal-browser';
import { environment } from '@/environments/environment';
import { Router } from '@angular/router';
import { clearMSALInteractionState } from '../utilities/utils';
import { LoadingService } from './loading.service';
import { CountdownService } from './countdown.service';
import { NotificationService } from '@/app/pages/notifications/notification.service';
import { getPath, PathUnit } from '@/app/consts/path.const';
import { RoleEnum, ROLE_PRIORITY } from '@/app/consts/role.const';
import { Brand } from '@/app/consts/brand.config';
import { AllAppLinks, getRolesByLink, isRouteProtected } from '@/app/consts/app-links.config';

export type UserProfile = {
  accountId?: string;
  displayName: string;
  email: string;
};

@Injectable({ providedIn: 'root' })
export class AuthService {
  private countdownService = inject(CountdownService);
  // private notificationService = inject(NotificationService);
  /**
   * get the current access token, prefer acquireTokenSilent, return null if failed
   */
  getAccessToken(): Observable<string | null> {
    return from(this.ensureInitialized().then(() => {
      const msalService = this.currentMsalService();
      const accounts = msalService.instance.getAllAccounts();
      if (!accounts || accounts.length === 0) return null;
      const account = msalService.instance.getActiveAccount() || accounts[0];
      const scopes = this.getScopes();
      return msalService.instance.acquireTokenSilent({
        account,
        scopes
      }).then(result => {
        console.log('Get Access Token From Memory:');
        localStorage.setItem('access_token', result.accessToken);
        // this.notificationService.initialize(result.accessToken);
        return result.accessToken;
      }).catch(() => {
        return null;
      });
    }));
  }

  /**
   * force refresh the token, if fails, logout and throw error
   */
  refreshToken(): Observable<string> {
    return from(this.ensureInitialized().then(async () => {
      const msalService = this.currentMsalService();
      const accounts = msalService.instance.getAllAccounts();
      if (!accounts || accounts.length === 0) {
        // only accounts is empty, means we don't have login state
        // need to clear local state and navigate to login
        console.log('No account, redirecting to login...');
        this.removeLocalLoginState();

        this.countdownService.show('No account, redirecting to login...', 3, () => {
          this.router.navigate([`/${getPath(PathUnit.Login)}`]);
        });

        throw new Error('No account');
      }
      const account = msalService.instance.getActiveAccount() || accounts[0];
      const scopes = this.getScopes();
      try {
        const result = await msalService.instance.acquireTokenSilent({
          account,
          scopes,
          forceRefresh: true
        });
        console.log('Refresh Access Token Success');
        localStorage.setItem('access_token', result.accessToken);
        return result.accessToken;
      } catch (e) {
        await this.logout();
        throw e;
      }
    }));
  }
  private router = inject(Router);

  private msalFactory = inject(MsalFactoryService);

  private _isInitialized = false;

  // 使用 Signal 来管理当前选择的认证类型
  private readonly _currentAuthType = signal<Brand>(
    this.getStoredAuthType() // 从存储中初始化
  );

  // 当前认证类型的 Signal
  readonly currentAuthType = this._currentAuthType.asReadonly();

  // 角色管理 Signals
  private readonly _userRoles = signal<RoleEnum[]>([]);
  private readonly _selectedRole = signal<RoleEnum | ''>('');
  
  // 只读的角色 Signals
  readonly userRoles = this._userRoles.asReadonly();
  readonly selectedRole = this._selectedRole.asReadonly();
  
  // 计算属性：可用角色（按优先级排序）
  readonly availableRoles = computed(() => {
    const roles = this._userRoles();
    return roles
      .filter(role => Object.prototype.hasOwnProperty.call(ROLE_PRIORITY, role))
      .sort((a, b) => (ROLE_PRIORITY[a] || 999) - (ROLE_PRIORITY[b] || 999));
  });

  // 当前 MsalService 的 Signal
  readonly currentMsalService = computed(() => {
    return this.msalFactory.getMsalService(this._currentAuthType());
  });

  // 当前 DynamicMsalService 的 Signal（如果需要）
  readonly currentDynamicMsalService = computed(() => {
    return this.msalFactory.getDynamicMsalService(this._currentAuthType());
  });

  // 当前 MsalBroadcastService 的 Signal
  readonly currentMsalBroadcastService = computed(() => {
    return this.msalFactory.getMsalBroadcastService(this._currentAuthType());
  });

  // 用于管理订阅的生命周期
  private readonly _destroying$ = new Subject<void>();

  constructor() {
    /* istanbul ignore next */
    // 使用 effect 监听认证类型变化并持久化
    effect(() => {
      const type = this._currentAuthType();
      this.persistAuthType(type);

      // 取消之前的订阅
      this._destroying$.next();

      // 重新订阅新的 MsalBroadcastService 事件
      this.subscribeToMsalEvents();

      // set initialized to false
      this._isInitialized = false;
    });

    // 监听角色变化，检查当前路由权限
    /* istanbul ignore next */
    effect(() => {
      const selectedRole = this._selectedRole();
      if (selectedRole) {
        this.validateCurrentRouteAccess();
      }
    });

    /* istanbul ignore next */
    this.initialize().catch((error) => {
      console.error('MSAL Initialize failed', error);
    });

    // 初始订阅
    this.subscribeToMsalEvents();
  }

  /**
   * 订阅 MSAL 事件
   */
  private subscribeToMsalEvents(): void {
    const broadcastService = this.currentMsalBroadcastService();
    // 1. 订阅交互状态
    broadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None
        ),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        console.log('MSAL 交互已完成，可以安全地进行其他操作');
        // 例如：检查登录状态、获取用户信息等
        // this.checkAccount();
      });

    // 2. 订阅特定事件（例如登录成功）
    broadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS
        ),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        console.log('Login Success', result);
        this.handleLoginSuccess(result)
      });

      broadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => 
          msg.eventType === EventType.LOGOUT_SUCCESS || 
          msg.eventType === EventType.LOGOUT_FAILURE 
        ),
        takeUntil(this._destroying$)
      )
      .subscribe((result: EventMessage) => {
        console.log('Logout', result);
        this.handleLogout();
      });

    // 3. 订阅其他感兴趣的事件，例如获取令牌失败
    broadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
            msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE
        ),
        takeUntil(this._destroying$)
      )
      .subscribe(async (result: EventMessage) => {
        if (result.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) {
          await this.handleTokenRefreshSuccess();
        } else {
          await this.handleTokenRefreshFailure();
        }
      });
  }

  private async handleAuthStatusChange(): Promise<void> {
    const accounts = this.currentMsalService().instance.getAllAccounts();

    // initialize notification service
    // if (accounts.length > 0 && !this.notificationService.isServiceInitialized()) {
    //   const token = await lastValueFrom(this.getAccessToken());
    //   if (token) {
    //     await this.notificationService.initialize(token);
    //   }
    // }
  }

  private async handleLoginSuccess(result: EventMessage): Promise<void> {
    if (result.payload) {
      // set account
      const authResult = result.payload as AuthenticationResult;
      this.currentMsalService().instance.setActiveAccount(
        authResult.account
      );
    }

    // initialize notification service
    const accounts = this.currentMsalService().instance.getAllAccounts();
    // if (accounts.length > 0 && !this.notificationService.isServiceInitialized()) {
    //   const token = await lastValueFrom(this.getAccessToken());
    //   if (token) {
    //     await this.notificationService.initialize(token);
    //   }
    // }
  }

  private handleLogout(): void {
    // this.notificationService.disconnect();
  }

  private async handleTokenRefreshSuccess(): Promise<void> {
    console.log('Token refresh succeeded, reconnecting notification service');
    const token = await lastValueFrom(this.getAccessToken());
    if (token) {
      // await this.notificationService.reconnect(token);
    }
  }

  private async handleTokenRefreshFailure(): Promise<void> {
    console.error('Token refresh failed, disconnect notification service');
    // await this.notificationService.disconnect();
  }

  ngOnDestroy() {
    // 在服务销毁时取消所有订阅
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  // 从存储中获取认证类型
  private getStoredAuthType(): Brand {
    try {
      const stored = localStorage.getItem('authType');
      return (stored as Brand) || 'cn'; // default to 'cn' if not set
    } catch (e) {
      console.warn('Cannot access localStorage, useDefault AuthType', e);
      return 'cn';
    }
  }

  // 持久化认证类型
  private persistAuthType(type: Brand): void {
    try {
      localStorage.setItem('authType', type);
    } catch (e) {
      console.warn('Cannot save to localStorage', e);
    }
  }

  initialRoles() {
    let roles: RoleEnum[] = [];

    if (this._userRoles().length === 0) {
      if(environment.environmentName === 'development') {
        roles = [RoleEnum.Admin, RoleEnum.Driver, RoleEnum.Lead, RoleEnum.Engineer, RoleEnum.Manager]; // TODO: mock roles
      } else {
        const msalService = this.currentMsalService();
        const accounts: AccountInfo[] = msalService.instance.getAllAccounts();
        if (accounts && accounts.length > 0) {
          // 从账号信息中获取角色声明
          roles = accounts[0].idTokenClaims?.['roles'] as RoleEnum[] || [];
          console.log("🚀 ~ AuthService ~ getCurrentUserRoles ~ roles:", roles)
        }
      }
      
      this._userRoles.set(roles);
    }

    this.restoreRoles();
  }

  restoreRoles() {
    const roles = this._userRoles();
    try {
      const storedRole = localStorage.getItem('selectedRole') as RoleEnum | null;
      if (storedRole && roles.includes(storedRole)) {
        this._selectedRole.set(storedRole);
        return;
      }
    } catch (e) {
      console.warn('Cannot read selected role from localStorage', e);
    }
    
    if (!this._selectedRole()) {
      const defaultRole = this.getDefaultRole(roles);
      this._selectedRole.set(defaultRole);
    }
  }

  getCurrentUserProfile(): UserProfile | null {
    const account = this.currentMsalService().instance.getActiveAccount();
    const accounts = this.currentMsalService().instance.getAllAccounts();
    const selectedAccount = account || accounts?.[0];
    if (!selectedAccount) {
      return null;
    }

    return {
      accountId: selectedAccount.homeAccountId,
      displayName: selectedAccount.name ?? selectedAccount.username ?? 'User',
      email: selectedAccount.username ?? '',
    };
  }

  hasRequiredRole(requiredRoles: (string | RoleEnum)[] | RoleEnum[]): boolean {
    if (!requiredRoles || !requiredRoles.filter(Boolean).length) {
      // forbid empty roles
      return false;
    }
    
    // 使用选择的角色而不是所有用户角色
    const selectedRole = this._selectedRole();
    if (!selectedRole) {
      return false;
    }
    
    return requiredRoles.some(role => role === selectedRole);
  }

  // 角色管理方法
  
  /**
   * 获取默认角色（最高优先级）
   */
  private getDefaultRole(roles: RoleEnum[]): RoleEnum | '' {
    const availableRoles = roles
      .filter(role => Object.prototype.hasOwnProperty.call(ROLE_PRIORITY, role))
      .sort((a, b) => (ROLE_PRIORITY[a] || 999) - (ROLE_PRIORITY[b] || 999));

    return availableRoles.length > 0 ? availableRoles[0] : '';
  }
  
  /**
   * 设置选择的角色
   */
  setSelectedRole(role: RoleEnum): void {
    const userRoles = this._userRoles();
    if (userRoles.includes(role)) {
      this._selectedRole.set(role);
      // 可以在这里添加持久化逻辑，比如存储到 localStorage
      try {
        localStorage.setItem('selectedRole', role);
      } catch (e) {
        console.warn('Cannot save selected role to localStorage', e);
      }
    } else {
      console.warn(`Role ${role} is not available for current user`);
    }
  }
  
  /**
   * 获取当前选择的角色
   */
  getSelectedRole(): RoleEnum | '' {
    return this._selectedRole();
  }
  
  
  /**
   * 重置角色选择
   */
  resetRoleSelection(): void {
    const roles = this._userRoles();
    const defaultRole = this.getDefaultRole(roles);
    this._selectedRole.set(defaultRole);
    
    try {
      localStorage.removeItem('selectedRole');
    } catch (e) {
      console.warn('Cannot remove selected role from localStorage', e);
    }
  }

  /**
   * 验证当前路由是否允许当前角色访问
   */
  private validateCurrentRouteAccess(): void {
    const currentUrl = this.router.url;
    const currentRoute = this.extractRouteFromUrl(currentUrl);
    
    // 检查当前路由是否需要角色验证
    if (this.isRouteAccessible(currentRoute)) {
      return; // 当前路由可以访问，不需要重定向
    }
    
    console.log(`当前路由 ${currentRoute} 不允许角色 ${this._selectedRole()} 访问，重定向到 Dashboard`);
    
    // 重定向到 Dashboard
    this.router.navigate([getPath(PathUnit.Dashboard)]);
  }
  
  /**
   * 从 URL 中提取路由路径
   */
  private extractRouteFromUrl(url: string): string {
    // 移除查询参数和片段
    const cleanUrl = url.split('?')[0].split('#')[0];
    // 移除前导斜杠
    return cleanUrl.startsWith('/') ? cleanUrl.substring(1) : cleanUrl;
  }
  
  /**
   * 检查指定路由是否允许当前角色访问
   */
  private isRouteAccessible(route: string): boolean {
    // Dashboard 总是可以访问
    if (route === getPath(PathUnit.Dashboard) || route === '') {
      return true;
    }
    
    // 检查路由是否需要角色验证
    if (!isRouteProtected(route)) {
      return true; // 不需要角色验证的路由总是可以访问
    }
    
    // 查找路由对应的角色要求
    const requiredRoles = getRolesByLink(route);
    
    // 如果找不到路由配置，允许访问（可能是动态路由）
    if (!requiredRoles || requiredRoles.length === 0) {
      return true;
    }
    
    // 检查当前选择的角色是否在要求的角色列表中
    const selectedRole = this._selectedRole();
    return requiredRoles.includes(selectedRole as RoleEnum);
  }
  
  /**
   * 手动检查路由权限（供外部调用）
   */
  checkRouteAccess(route: string): boolean {
    return this.isRouteAccessible(route);
  }

  getScopes() {
    const brand = this._currentAuthType();
    const msalMap = environment.msal as Record<string, { scopes?: string[] } | undefined>;
    const brandConfig = msalMap[brand] ?? msalMap['cn'];
    return brandConfig?.scopes ?? [];
  }

  // 清除存储的认证类型
  clearStoredAuthType(): void {
    try {
      localStorage.removeItem('authType');
    } catch (e) {
      console.warn('Cannot clear localStorage', e);
    }
  }

  // 设置认证类型
  async setAuthType(type: Brand): Promise<void> {
    // 如果已经是当前类型且已初始化，直接返回
    if (this._currentAuthType() === type && this._isInitialized) {
      return;
    }

    // 设置新的认证类型
    this._currentAuthType.set(type);

    // 重置初始化状态
    this._isInitialized = false;

    // 等待初始化完成
    await this.initialize();

    // 初始化完成后，可以在这里添加其他逻辑
    console.log(`Auth type switched to ${type} and initialized`);
  }

  async initialize(): Promise<void> {
    if (this._isInitialized) {
      return; // 已经初始化过了
    }
    console.log(this._currentAuthType());
    // 确保MSAL实例已初始化
    const msalService = this.currentMsalService();
    try {
      // debugger
      await msalService.instance.initialize();
      this._isInitialized = true;

      this.initialRoles();
      this.handleRedirectCallback();
    } catch (error) {
      console.error('MSAL Initialize failed', error);
      this._isInitialized = false;
      throw error;
    }
  }

  // 确保初始化的辅助方法
  private async ensureInitialized(): Promise<void> {
    if (!this._isInitialized) {
      await this.initialize();
    }
  }

  // handleRedirectCallback
  handleRedirectCallback(): void {
    this.currentMsalService()
      .handleRedirectObservable()
      .subscribe({
        next: (result) => {
          if (result) {
            console.log('login Succces', result);
            this.currentMsalService().instance.setActiveAccount(result.account);
            this.initialRoles();
            localStorage.setItem('access_token', result.accessToken);
            this.router.navigate([`/${getPath(PathUnit.Dashboard)}`]);
          }
        },
        error: (error) => {
          console.error('Redirect Callback Error', error);
        },
      });
  }

  // 登录 - 使用当前选择的认证服务
  loginRedirect(loginRequest: RedirectRequest): Promise<void> {
    return this.initialize().then(() => {
      this.currentMsalService().loginRedirect(loginRequest);
      // 由于 loginRedirect 是同步的，我们无法知道它何时完成
      // 所以返回一个已解决的 Promise
      return Promise.resolve();
    });
  }

  // 登出 - 使用当前选择的认证服务
  async logout() {
    if (this.isLoggedIn()) {
      // remove local login state first
      this.removeLocalLoginState();
      // then call msal logout
      await this.logoutMSAL();
    } else {
      console.warn('当前没有用户登录');
      return;
    }
  }

  private removeLocalLoginState() {
    // pay attention: do not clear authType here, because it's user preference
    sessionStorage.clear(); // only clear session storage
    localStorage.clear();
  }

  private async logoutMSAL() {
    // call msal logout, then clear interaction state
    await this.currentMsalService().logoutRedirect();
    clearMSALInteractionState();
  }
  
  // logout and clear authType (authType is user preference, so only clear when full logout)
  fullLogout() {
    this.clearStoredAuthType();
    this.logout();
  }

  // 检查登录状态 - 确保先初始化
  isLoggedIn(): boolean {
    if (!this._isInitialized) {
      console.warn('MSAL 尚未初始化，尝试检查登录状态');
      return false;
    } else {
      try {
        const accounts = this.currentMsalService().instance.getAllAccounts();
        return accounts.length > 0;
      } catch (e) {
        console.error('检查登录状态时出错', e);
        return false;
      }
    }
  }

  // 异步检查登录状态（推荐用于路由守卫）
  async checkLoggedIn(): Promise<boolean> {
    await this.ensureInitialized();
    return this.isLoggedIn();
  }
}